<template>
  <ProductReportDrawer :open="isOpen" :without-backdrop="withoutBackdrop" @close="handleCloseDrawer">
    <ProductReportDetails
      :product-name="actor.snapshot.value.context.productInfo?.productName || null"
      :image-url="actor.snapshot.value.context.productInfo?.imageUrl || null"
      :price="actor.snapshot.value.context.productInfo?.price ?? null"
      :url="actor.snapshot.value.context.productInfo?.productUrl || null"
      :is-available="actor.snapshot.value.context.isAvailable || false"
      :is-loading="actor.snapshot.value.hasTag('fetchingProductInformation')"
      :is-bestseller="actor.snapshot.value.context.productInfo?.isBestseller || false"
      :recommendation-status="actor.snapshot.value.context.productStatus"
      @update-recommendation-status="handleRecommendationStatus"
    />

    <div class="tw:border-b tw:border-default-200 tw:max-w-full tw:py-4 tw:mb-6" />

    <ProductReportAnalytics
      :viewed="actor.snapshot.value.context.statistics?.aggregated.totalViewedCount ?? null"
      :added-to-cart="actor.snapshot.value.context.statistics?.aggregated.totalAddedToCartCount ?? null"
      :orders="actor.snapshot.value.context.statistics?.aggregated.totalOrderedByCustomerCount ?? null"
      :ordered-copies="actor.snapshot.value.context.statistics?.aggregated.totalOrderedCopiesCount ?? null"
      :conversion-added-to-cart="actor.snapshot.value.context.statistics?.aggregated.conversionAddedToCart ?? null"
      :conversion-ordered="actor.snapshot.value.context.statistics?.aggregated.conversionOrderedByCustomer ?? null"
      :date-from="mapUnixToLocalTimeDate(actor.snapshot.value.context.dateFrom)"
      :date-to="mapUnixToLocalTimeDate(actor.snapshot.value.context.dateTo)"
      :revenue="getCalculatedRevenue"
      :statistics="actor.snapshot.value.context.statistics?.daily || []"
      :is-loading="
        actor.snapshot.value.hasTag('fetchingProductStatistics') ||
        actor.snapshot.value.hasTag('refetchProductsStatistics')
      "
      @change-date="handleChangeDate"
    />

    <ProductReportDebugDetails
      v-if="isDev"
      :tracker-product-id="actor.snapshot.value.context.trackerProductId"
      :app-id-product-id="actor.snapshot.value.context.appIdProductId"
    />
  </ProductReportDrawer>
</template>

<script setup lang="ts">
import ProductReportDrawer from "./ProductReportDrawer.vue";
import { computed, provide, watch } from "vue";
import { inspector } from "vue-app/utils/xstate";
import { useMachine } from "@xstate-5/vue";
import { productReportMachine, symbolProductReportMachine } from "./ProductReport.machine";
import ProductReportDetails from "./ProductReportDetails/ProductReportDetails.vue";
import { formatPrice } from "vue-app/utils/functions";
import ProductReportAnalytics from "./ProductReportAnalytics/ProductReportAnalytics.vue";
import { mapUnixToLocalTimeDate } from "vue-app/datesDatepicker/datesDatepicker";
import { PRODUCT_RECOMMENDATION_STATUS } from "vue-app/components/ProductReport/ProductReport.enum";
import ProductReportDebugDetails from "vue-app/components/ProductReport/ProductReportDebugDetails/ProductReportDebugDetails.vue";

interface Props {
  isOpen: boolean;
  appIdProductId: string | null;
  trackerProductId: string | null;
  withoutBackdrop?: boolean;
}

interface Emits {
  updateRecommendationStatus: [trackerProductId: number, status: PRODUCT_RECOMMENDATION_STATUS];
  close: [];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const actor = useMachine(productReportMachine, { inspect: inspector().inspect });
provide(symbolProductReportMachine, actor);

const isDev = window.EDRONE_CONTEXT.user.isDev;

const getCalculatedRevenue = computed(() => {
  const { productInfo, statistics } = actor.snapshot.value.context;

  if (productInfo?.price && statistics?.aggregated?.totalOrderedCopiesCount) {
    const calculatedRevenue = productInfo.price * statistics.aggregated.totalOrderedCopiesCount;
    const revenue = formatPrice(calculatedRevenue);
    return `${revenue}`;
  }

  return formatPrice(0);
});

watch(
  props,
  ({ appIdProductId, trackerProductId, isOpen }) => {
    if (isOpen) {
      if (!appIdProductId || !trackerProductId) return;

      actor.send({
        type: "ASSIGN_INITIAL_DATA",
        payload: { appIdProductId: appIdProductId, trackerProductId: trackerProductId },
      });
    }
  },
  { immediate: true }
);

const handleCloseDrawer = () => {
  emit("close");
  actor.send({ type: "RESET_CONTEXT" });
};

const handleChangeDate = ([dateFrom, dateTo]: [Date, Date]) => {
  actor.send({ type: "CHANGE_DATE_RANGE", payload: { dateFrom, dateTo } });
};

const handleRecommendationStatus = (status: PRODUCT_RECOMMENDATION_STATUS) => {
  actor.send({ type: "CHANGE_RECOMMENDATION_STATUS", payload: status });
  emit("updateRecommendationStatus", Number(actor.snapshot.value.context.trackerProductId), status);
};
</script>
